import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SectionHeading } from '../components/SectionHeading';
import { CTASection } from '../components/CTASection';
const principles = [{
  title: 'Trust',
  text: 'Clients rely on us with responsibilities that matter. We treat that position seriously in every decision we make.'
}, {
  title: 'Responsibility',
  text: 'Assets, distributions, obligations and records are administered carefully and in accordance with the governing documents.'
}, {
  title: 'Privacy',
  text: 'Financial and personal matters are handled discreetly and shared only with those who need to be involved.'
}, {
  title: 'Personal attention',
  text: 'Each situation is different. We take the time to understand the people and circumstances behind the paperwork.'
}, {
  title: 'Professional administration',
  text: 'Organized processes, accurate documentation and consistent follow-through across every matter we handle.'
}, {
  title: 'Communication',
  text: 'Families, beneficiaries and professional advisors are kept informed with clear, timely updates.'
}];
export function About() {
  return <>
      <PageHeader eyebrow="About Us" title="A Professional Partner for Important Responsibilities" description="We provide fiduciary and administrative support to individuals and families who require dependable assistance with trusts, estates, personal affairs and ongoing care-related responsibilities." crumbs={[{
      label: 'About Us'
    }]} data-id="element-228" data-mp="1yjn3jzzimsly" />

      <section className="bg-white" data-id="element-229" data-mp="2masjef5ghkw9">
        <div className="mx-auto grid max-w-content items-center gap-12 px-6 py-16 sm:py-24 lg:grid-cols-[1.05fr_0.95fr] lg:gap-20 lg:px-8" data-id="element-230" data-mp="2i74c7xkqkrbj">
          <div data-id="element-231" data-mp="2w7f6lal98o1g">
            <SectionHeading eyebrow="Our Approach" title="Careful administration, handled quietly and consistently" data-id="element-232" data-mp="2w4sucxnf5kcw" />
            <p className="mt-6 text-[17px] leading-relaxed text-navy-600" data-id="element-233" data-mp="1ovxspsc3mqc6">
              Responsibilities such as administering a trust, settling an estate or
              managing another person's day-to-day affairs require patience,
              organization and discretion. We take on that work so families can focus
              on the people involved rather than the paperwork.
            </p>
            <p className="mt-4 text-[17px] leading-relaxed text-navy-600" data-id="element-234" data-mp="27kcnmqpjeoi9">
              Our services are practical and clearly defined: administering assets,
              paying bills, keeping records, coordinating care and communicating with
              beneficiaries, family members and professional advisors. We work
              steadily, document what we do and remain available for the long term.
            </p>
            <Link to="/services" className="mt-8 inline-flex items-center gap-2 text-[15px] font-medium text-navy-800 transition-colors duration-150 ease-out hover:text-gold-600" data-id="element-235" data-mp="m9w14pz54sk1">
              Explore Our Services
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-236" data-mp="evlp1vpiyo0t" />
            </Link>
          </div>
          <img src="https://cdn.magicpatterns.com/patterns/generated-images/da897203-ec38-4f7a-9fa7-1224b019c96b.jpg" alt="A quiet private office with bookshelves and a conference table" className="aspect-[4/3] w-full object-cover" loading="lazy" data-id="element-237" data-mp="9ylhfgd11d7l" />
        </div>
      </section>

      <section className="border-y border-navy-100 bg-navy-50" data-id="element-238" data-mp="xl1pyk54gihr">
        <div className="mx-auto max-w-content px-6 py-16 sm:py-24 lg:px-8" data-id="element-239" data-mp="sn04jsk84734">
          <SectionHeading eyebrow="What Guides Our Work" title="The principles behind every engagement" data-id="element-240" data-mp="omzjhp2mcxn3" />
          <div className="mt-12 grid gap-px bg-navy-100 sm:grid-cols-2 lg:grid-cols-3" data-id="element-241" data-mp="3dftulmwt7ovz">
            {principles.map(principle => <div key={principle.title} className="flex flex-col bg-navy-50 p-7" data-id="element-242" data-mp="113z9pe9p2ai5">
                <h3 className="font-serif text-[20px] text-navy-950" data-id="element-243" data-mp="1125z5nvyn64v">
                  {principle.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-navy-600" data-id="element-244" data-mp="r62vgdlgmzft">
                  {principle.text}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      <section className="bg-white" data-id="element-245" data-mp="1ju767diw22pd">
        <div className="mx-auto grid max-w-content items-center gap-12 px-6 py-16 sm:py-24 lg:grid-cols-2 lg:gap-20 lg:px-8" data-id="element-246" data-mp="1jv07w02zq20n">
          <img src="https://cdn.magicpatterns.com/patterns/generated-images/8d62bd1d-6158-4e6e-a4c8-447e4447592c.jpg" alt="A care coordinator reviewing a schedule with an elderly woman at home" className="aspect-[4/3] w-full object-cover" loading="lazy" data-id="element-247" data-mp="1or7c9ofqn5gh" />
          <div data-id="element-248" data-mp="3fyw4ifb1lum">
            <SectionHeading eyebrow="Who We Support" title="Dependable support for the people who need it" data-id="element-249" data-mp="3gh0k2c4f6dm" />
            <ul className="mt-8 space-y-4 text-[16px] leading-relaxed text-navy-700" data-id="element-250" data-mp="3s46mr0w2b7ev">
              <li className="border-l-2 border-gold-400 pl-5" data-id="element-251" data-mp="3n2hchngnyxru">
                Individuals and families managing trusts, estates and personal
                affairs.
              </li>
              <li className="border-l-2 border-gold-400 pl-5" data-id="element-252" data-mp="x3tm3wnyr20">
                Seniors who need assistance with bills, housing, appointments and
                daily administration.
              </li>
              <li className="border-l-2 border-gold-400 pl-5" data-id="element-253" data-mp="34du8z9e4yggd">
                Beneficiaries who need clear communication and reliable records.
              </li>
              <li className="border-l-2 border-gold-400 pl-5" data-id="element-254" data-mp="3ea5kvy9td4aa">
                Attorneys, accountants and care professionals seeking a steady
                administrative partner.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <CTASection title="Speak With Our Team" description="If you are considering professional fiduciary support, we are glad to talk through your circumstances." primaryLabel="Contact Us" data-id="element-255" data-mp="2ljx9cp9s9i4o" />
    </>;
}